// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// Toast notifications are handled at the index.js level

import MainLayout from './layouts/MainLayout';
import DashboardLayout from './layouts/DashboardLayout';

import VideoLanding from './components/VideoLanding';
import Home from './components/Home';
import Login from './components/Login';
import Signup from './components/Signup';
import AdminDashboard from './components/AdminDashboard';
import UserDashboard from './components/Dashboard';
import RecordVideo from './components/RecordVideo';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionRequired from './components/SubscriptionRequired';
import Unauthorized from './components/Unauthorized';
import UserProfile from './components/UserProfile';
import PurchaseFlow from './components/PurchaseFlow';
import LegalDocumentPage from './components/LegalDocumentPage';
import Debug from './components/Debug';
import VanityResolver from './components/VanityResolver';

// Blog Components
import BlogHome from './components/blog/BlogHome';
import BlogPostDetail from './components/blog/BlogPostDetail';
import BlogAdminDashboard from './components/blog/admin/BlogAdminDashboard';
import { BlogProvider } from './contexts/BlogContext';

function App() {
  return (
    <React.StrictMode>
      <Router>
        <HelmetProvider>
          <BlogProvider>
          <Routes>
          {/* Main Layout (Includes Navbar and Footer) */}
          <Route element={<MainLayout />}>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            
            {/* Video Landing Page (Publicly Accessible) */}
            <Route path="/:userId/introreel/:videoId" element={<VideoLanding />} />

            {/* Blog Routes */}
            <Route path="/blog" element={<BlogHome />} />
            <Route path="/blog/:slug" element={<BlogPostDetail />} />
            
            {/* Blog Admin Routes */}
            <Route
              path="/blog/admin/*"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <BlogAdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Two purchase routes - one protected, one for new signups */}
            <Route
              path="/purchase-direct"
              element={<PurchaseFlow />}
            />
            <Route
              path="/purchase"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <PurchaseFlow />
                </ProtectedRoute>
              }
            />

            {/* Dashboard Layout for authenticated user routes */}
            <Route
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route
                path="/dashboard"
                element={
                  <SubscriptionRequired>
                    <Navigate to="/dashboard/home" replace />
                  </SubscriptionRequired>
                }
              />
              
              <Route
                path="/dashboard/home"
                element={
                  <SubscriptionRequired>
                    <UserDashboard />
                  </SubscriptionRequired>
                }
              />
              
              <Route
                path="/record"
                element={
                  <SubscriptionRequired>
                    <RecordVideo />
                  </SubscriptionRequired>
                }
              />
              
              <Route
                path="/profile"
                element={<UserProfile />}
              />
            </Route>

            {/* Debug Route */}
            <Route path="/debug" element={<Debug />} />
            
            {/* Legal Document Pages */}
            <Route path="/legal/:documentType" element={<LegalDocumentPage />} />
            
            {/* Vanity URL Resolver (Dynamic Profile Links) */}
            <Route path="/:vanityId" element={<VanityResolver />} />

            {/* Unauthorized and Catch-All Redirect */}
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        </BlogProvider>
      </HelmetProvider>
    </Router>
    </React.StrictMode>
  );
}

export default App;
